import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { useEffect, useState, useCallback } from "react";

export default function Header({ isLoggedIn, setIsLoggedIn }) {
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [searchName, setSearchName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Get user name from cookie
  useEffect(() => {
    const userCookie = Cookies.get("user");
    if (userCookie) {
      try {
        const user = JSON.parse(userCookie);
        setUserName(user.name);
      } catch (err) {
        console.error("Failed to parse user cookie", err);
        setUserName("");
      }
    }
  }, [isLoggedIn]);

  const handleLogout = () => {
    Cookies.remove("authToken");
    Cookies.remove("user");
    setIsLoggedIn(false);
    setDrawerOpen(false);
    navigate("/login");
  };

  const searchUser = useCallback(async (name) => {
    if (!name.trim()) {
      setResult(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/findbyname`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch");
      }
      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchName(value);
    if (typingTimeout) clearTimeout(typingTimeout);
    setTypingTimeout(setTimeout(() => searchUser(value), 500));
  };

  const handleSearchClick = () => {
    if (typingTimeout) clearTimeout(typingTimeout);
    searchUser(searchName);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (typingTimeout) clearTimeout(typingTimeout);
      searchUser(searchName);
    }
  };

  const handleClick = (userId) => {
    setResult(null);
    setSearchName("");
    setDrawerOpen(false);
    navigate(`/user/${userId}`);
  };

  // Drawer contents
  const drawerContent = (
    <Box sx={{ width: 240 }} role="presentation">
      <List>
        {isLoggedIn && (
          <ListItem>
            <ListItemText primary={`👋 ${userName}`} />
          </ListItem>
        )}
        {!isLoggedIn ? (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => { setDrawerOpen(false); navigate("/login"); }}>
                <ListItemText primary="Login" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={() => { setDrawerOpen(false); navigate("/register"); }}>
                <ListItemText primary="Register" />
              </ListItemButton>
            </ListItem>
          </>
        ) : (
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout}>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
      <Divider />
      {result && (
        <List>
          <ListItem>
            <ListItemText primary="Search Results" />
          </ListItem>
          {(Array.isArray(result) ? result : [result]).map((user, idx) => (
            <ListItemButton key={idx} onClick={() => handleClick(user._id)}>
              <ListItemText primary={`${user.name} (${user.review_count ?? 0})`} />
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  );

  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: "#1976d2" }}>
        <Toolbar
          sx={{
            flexWrap: { xs: "wrap", sm: "nowrap" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          {/* Menu icon opens Drawer */}
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            sx={{ mr: 2 }}
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon />
          </IconButton>

          {/* App title */}
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: { xs: 1, sm: 0 },
              cursor: "pointer",
              mb: { xs: 1, sm: 0 },
            }}
            onClick={() => navigate("/")}
          >
            Review My Escort
          </Typography>

          {/* Search box */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: 1,
              width: { xs: "100%", sm: "auto" },
              mb: { xs: 1, sm: 0 },
              mr: { xs: 0, sm: 4 },
            }}
          >
            <TextField
              size="small"
              placeholder="Search escort"
              value={searchName}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              sx={{
                flex: 1,
                background: "#fff",
                borderRadius: 1,
                minWidth: { xs: "100%", sm: "200px" },
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearchClick}
              sx={{
                backgroundColor: "#fafafa",
                color: "#0d47a1",
                "&:hover": { backgroundColor: "#f0f0f0" },
              }}
            >
              Search
            </Button>
            {loading && <CircularProgress size={24} sx={{ ml: { sm: 1 } }} />}
          </Box>

          {/* Auth buttons (stay visible in header) */}
          <Box sx={{ display: "flex", gap: 1 }}>
            {isLoggedIn && userName && (
              <Typography
                variant="body1"
                sx={{ display: { xs: "none", sm: "block" }, alignSelf: "center" }}
              >
                👋 {userName}
              </Typography>
            )}

            {!isLoggedIn ? (
              <>
                <Button
                  onClick={() => navigate("/login")}
                  sx={{
                    backgroundColor: "#fafafa",
                    color: "#0d47a1",
                    "&:hover": { backgroundColor: "#f0f0f0" },
                  }}
                >
                  Login
                </Button>
                <Button
                  onClick={() => navigate("/register")}
                  sx={{
                    backgroundColor: "#fafafa",
                    color: "#0d47a1",
                    "&:hover": { backgroundColor: "#f0f0f0" },
                  }}
                >
                  Register
                </Button>
              </>
            ) : (
              <Button
                onClick={handleLogout}
                sx={{
                  backgroundColor: "#fafafa",
                  color: "#0d47a1",
                  "&:hover": { backgroundColor: "#f0f0f0" },
                }}
              >
                Logout
              </Button>
            )}
          </Box>
        </Toolbar>

        {/* Search results section below header */}
        <Box sx={{ p: 2, background: "#ffffff" }}>
          {error && (
            <div style={{ color: "red", marginBottom: "8px" }}>Error: {error}</div>
          )}
          {result && (
            <Box
              sx={{
                p: 2,
                background: "#ffffff",
                borderRadius: 2,
                mt: 1,
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: "#1a237e", fontWeight: 600, mb: 1 }}
              >
                Search Result{Array.isArray(result) ? "s" : ""}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {(Array.isArray(result) ? result : [result]).map((user, idx) => (
                  <Box
                    key={idx}
                    onClick={() => handleClick(user._id)}
                    sx={{
                      border: "2px solid #1976d2",
                      borderRadius: 2,
                      p: 1.5,
                      background: "#e3f2fd",
                      color: "#0d47a1",
                      fontWeight: 500,
                      cursor: "pointer",
                      minWidth: "180px",
                      transition: "background 0.3s",
                      "&:hover": { background: "#bbdefb" },
                    }}
                  >
                    <strong>{user.name}</strong> ({user.review_count ?? 0} reviews)
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </AppBar>

      {/* Drawer itself */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        {drawerContent}
      </Drawer>
    </>
  );
}
